import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Download, CheckCircle, ArrowRight, Share, PlusSquare, HeartPulse, Zap, Bell, X, FileText } from 'lucide-react';

// Page d'installation de la Web App mobile Assisteur (PWA) — branding DNA-24.
const InstallAssisteurApp = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const installUrl = `${window.location.origin}/install-assisteur`;

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw-assisteur.js', { scope: '/' }).catch((e) => console.error('[PWA Assisteur] SW:', e));
    }
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const android = /android/i.test(ua);
    setIsIOS(iOS);
    setIsMobile(iOS || android || /mobile/i.test(ua));
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setDeferredPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSInstructions(true);
    }
  };

  const goToApp = () => navigate('/assisteur-dashboard');

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#2D634A] to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-[#1E4D38]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-[#1E4D38]" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Application installée !</h1>
          <p className="text-slate-600 mb-6">L'application DNA-24 Assisteur est déjà installée sur votre appareil.</p>
          <button onClick={goToApp} data-testid="install-assisteur-goto"
            className="w-full py-4 bg-gradient-to-r from-[#1E4D38] to-[#2D634A] text-white rounded-xl font-semibold hover:opacity-95 transition-all flex items-center justify-center gap-2">
            Accéder à mon espace <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#2D634A] to-slate-900" data-testid="install-assisteur-page">
      <div className="p-6 text-center pt-12">
        <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg p-3">
          <img src="/dna/dna24-logo.png" alt="DNA-24" className="max-h-full max-w-full object-contain" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">DNA-24 Assisteur</h1>
        <p className="text-rose-200">Votre espace assisteur, dans votre poche</p>
      </div>

      <div className="px-4 pb-8">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md mx-auto overflow-hidden">
          {/* QR code */}
          <div className="p-6 border-b border-slate-100 flex flex-col items-center">
            <p className="text-sm font-semibold text-slate-900 mb-3">Scannez pour installer sur votre téléphone</p>
            <div className="p-3 bg-white rounded-2xl border-2 border-slate-100 shadow-sm" data-testid="install-assisteur-qr">
              <QRCodeSVG value={installUrl} size={172} fgColor="#1E4D38" level="M" includeMargin={false} />
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">Ouvrez l'appareil photo de votre smartphone et visez ce QR code.</p>
          </div>

          {/* Features */}
          <div className="p-6 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-4">Fonctionnalités</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1E4D38]/10 rounded-lg flex items-center justify-center"><HeartPulse size={20} className="text-[#1E4D38]" /></div>
                <div><p className="font-medium text-slate-900">Mes assistances</p><p className="text-sm text-slate-500">Suivez vos dossiers en un clic</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center"><FileText size={20} className="text-teal-600" /></div>
                <div><p className="font-medium text-slate-900">Mes factures</p><p className="text-sm text-slate-500">Consultez et téléchargez vos factures</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Bell size={20} className="text-blue-600" /></div>
                <div><p className="font-medium text-slate-900">Notifications</p><p className="text-sm text-slate-500">Restez informé en temps réel</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><Zap size={20} className="text-emerald-600" /></div>
                <div><p className="font-medium text-slate-900">Accès instantané</p><p className="text-sm text-slate-500">Depuis votre écran d'accueil</p></div>
              </div>
            </div>
          </div>

          {/* Install button */}
          <div className="p-6">
            {deferredPrompt ? (
              <button onClick={handleInstallClick} data-testid="install-assisteur-btn"
                className="w-full py-4 bg-gradient-to-r from-[#1E4D38] to-[#2D634A] text-white rounded-xl font-semibold hover:opacity-95 transition-all flex items-center justify-center gap-3 shadow-lg">
                <Download size={24} /> Installer l'application
              </button>
            ) : isIOS ? (
              <button onClick={handleInstallClick} data-testid="install-assisteur-btn-ios"
                className="w-full py-4 bg-gradient-to-r from-[#1E4D38] to-[#2D634A] text-white rounded-xl font-semibold hover:opacity-95 transition-all flex items-center justify-center gap-3 shadow-lg">
                <Download size={24} /> Installer sur iPhone/iPad
              </button>
            ) : (
              <div className="text-center">
                <p className="text-slate-600 mb-4">
                  {isMobile ? "Votre navigateur va proposer l'installation automatiquement." : "Scannez le QR Code ci-dessus depuis votre mobile pour installer l'app."}
                </p>
                <button onClick={goToApp}
                  className="w-full py-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl font-semibold hover:from-slate-800 hover:to-slate-900 transition-all flex items-center justify-center gap-2">
                  Continuer vers mon espace <ArrowRight size={20} />
                </button>
              </div>
            )}
            {(deferredPrompt || isIOS) && (
              <button onClick={goToApp} className="w-full mt-3 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Continuer sans installer →
              </button>
            )}
          </div>
        </div>
      </div>

      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b border-slate-100 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-lg font-bold text-slate-900">Installation sur iOS</h2>
              <button onClick={() => setShowIOSInstructions(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={24} className="text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#1E4D38]/10 rounded-full flex items-center justify-center text-[#1E4D38] font-bold">1</div>
                <div>
                  <p className="font-semibold text-slate-900 mb-2">Appuyez sur le bouton Partager</p>
                  <div className="bg-slate-100 rounded-xl p-4 flex items-center justify-center"><Share size={32} className="text-blue-500" /></div>
                  <p className="text-sm text-slate-500 mt-2">En bas de l'écran dans Safari</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#1E4D38]/10 rounded-full flex items-center justify-center text-[#1E4D38] font-bold">2</div>
                <div>
                  <p className="font-semibold text-slate-900 mb-2">Faites défiler et appuyez sur</p>
                  <div className="bg-slate-100 rounded-xl p-4 flex items-center gap-3"><PlusSquare size={28} className="text-slate-700" /><span className="font-medium text-slate-900">Sur l'écran d'accueil</span></div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#1E4D38]/10 rounded-full flex items-center justify-center text-[#1E4D38] font-bold">3</div>
                <div>
                  <p className="font-semibold text-slate-900 mb-2">Confirmez en appuyant sur "Ajouter"</p>
                  <div className="bg-[#1E4D38]/5 border border-[#1E4D38]/20 rounded-xl p-4 text-center"><CheckCircle size={32} className="text-[#1E4D38] mx-auto mb-2" /><p className="text-[#1E4D38] text-sm">L'app apparaîtra sur votre écran d'accueil</p></div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100">
              <button onClick={() => setShowIOSInstructions(false)} className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200">J'ai compris</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallAssisteurApp;
